import { Loader2 } from "lucide-react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { homePathForUser, useAuthStore } from "@/client/features/auth/auth-store";

/**
 * Routes whose authority is the URL token, not the session. A visitor following
 * an invite / reset / verify link is frequently already signed in (often as a
 * different principal), so bouncing them to home would dead-end the flow before
 * the page can act on the token. These must render regardless of session state.
 */
const TOKEN_ROUTES = new Set(["/accept-invite", "/reset-password", "/verify-email"]);

/**
 * Layout for the public auth pages: centered card on a muted backdrop. Signed-in
 * principals are bounced to their own area so they never see the login form -
 * except on token-carrying routes (see TOKEN_ROUTES), which always render.
 */
export function AuthLayout() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const { pathname } = useLocation();

  if (status === "loading") {
    return (
      <div className="flex min-h-svh items-center justify-center" aria-busy="true">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (status === "authenticated" && user && !TOKEN_ROUTES.has(pathname)) {
    return <Navigate to={homePathForUser(user)} replace />;
  }

  return (
    <div className="bg-muted/30 flex min-h-svh flex-col items-center justify-center gap-6 p-4">
      <div className="flex items-center gap-2">
        <span className="text-xl font-semibold tracking-tight">Mantle</span>
        <span className="text-muted-foreground text-sm">Portal</span>
      </div>
      <div className="w-full max-w-sm">
        <Outlet />
      </div>
    </div>
  );
}
